import { useState, useEffect } from 'react'
import { CloseIcon, FeedbackIcon } from '../icons/Icons'
import {
  loadMemory,
  loadMessages,
  loadLatex,
  loadExtractedCV,
} from '../../services/memoryStorage'
import styles from './FeedbackModal.module.css'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [feedback, setFeedback] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFeedback('')
      setSubmitStatus('idle')
      setErrorMessage('')
    }
  }, [isOpen])

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const collectContext = () => {
    const memory = loadMemory()
    const messages = loadMessages()
    const latex = loadLatex()
    const extractedCV = loadExtractedCV()

    return {
      memory: memory || '(empty)',
      conversationHistory: messages.length > 0
        ? messages.map(m => `[${m.role}]: ${m.content}`).join('\n\n')
        : '(no conversation)',
      latex: latex || '(no LaTeX)',
      extractedCV: extractedCV
        ? `Filename: ${extractedCV.filename}\n\nText:\n${extractedCV.text}`
        : '(no uploaded CV)',
    }
  }

  const handleSubmit = async () => {
    if (!feedback.trim()) return

    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')

    try {
      const context = collectContext()

      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          feedback: feedback.trim(),
          context,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string }
        throw new Error(errorData.error || `Failed to submit feedback (${response.status})`)
      }

      setSubmitStatus('success')
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      setSubmitStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleWrapper}>
            <FeedbackIcon className={styles.titleIcon} />
            <h2 className={styles.title}>Send Feedback</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} title="Close">
            <CloseIcon className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.content}>
          <p className={styles.description}>
            Help us improve! Your feedback will be submitted as a GitHub issue, along with your current session context (conversation history, resume data) to help us understand the issue.
          </p>

          <textarea
            className={styles.textarea}
            placeholder="Describe your feedback, bug report, or feature request..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            disabled={isSubmitting || submitStatus === 'success'}
            rows={6}
            autoFocus
          />

          {submitStatus === 'error' && (
            <div className={styles.error}>{errorMessage}</div>
          )}

          {submitStatus === 'success' && (
            <div className={styles.success}>Feedback submitted successfully! Thank you.</div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={isSubmitting || !feedback.trim() || submitStatus === 'success'}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  )
}
